import axios, { AxiosRequestConfig } from 'axios';
import {
  captureException,
  createAxiosInterceptor,
  DISTRIBUTED_TRACE_HEADER,
} from '@tracewayapp/react';
// config
import { HOST_API } from 'src/config-global';

export const INTERNAL_SERVER_ERROR = "An unexpected error has occured. We're looking into it.";
export const UNKNOWN_ERROR =
  'An unexpected error has occured. Please check your internet connection.';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({ baseURL: HOST_API, withCredentials: true });

// The Traceway SDK only propagates traceway-trace-id on same-origin requests,
// and the API lives on another origin — stamp every request ourselves and
// capture failures with the same id so frontend and backend errors link up.
axiosInstance.interceptors.request.use(createAxiosInterceptor());

axiosInstance.interceptors.response.use(
  (res) => res,
  (error) => {
    captureException(error, {
      distributedTraceId: error?.config?.headers?.[DISTRIBUTED_TRACE_HEADER] || undefined,
    });
    return Promise.reject(error);
  }
);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosInstance.get(url, { ...config });

  return res.data;
};

// ----------------------------------------------------------------------

export type ParametrizedEndpoint = {
  endpoint: string;
  params: string[];
};
export type Endpoint = ParametrizedEndpoint | string;

export function formatEndpoint(endpoint: Endpoint, data: any) {
  if (typeof endpoint === 'string') {
    return endpoint;
  }
  endpoint = endpoint as ParametrizedEndpoint;

  let finalEndpoint = endpoint.endpoint;
  endpoint.params.forEach((param) => {
    finalEndpoint = finalEndpoint.split(`:${param}`).join(encodeURIComponent(data[param]));
  });
  return finalEndpoint;
}

export const endpoints = {
  auth: {
    current: '/current',
    login: '/login',
    register: '/register',
    changeOrganization: {
      endpoint: '/changeOrganization/:targetOrganizationId',
      params: ['targetOrganizationId']
    },
  },
  dashboardData: {
    find: '/dashboardData',
  },
  cameraNotifications: {
    findAll: {
      endpoint: '/cameraNotifications/:fromDate/:toDate',
      params: ["fromDate", "toDate"]
    },
  },
  cameras: {
    findAll: '/cameras',
    assign: '/cameras/assign',
    livePhoto: '/cameras/livePhoto',
    liveVideo: '/cameras/liveVideo',
    requestHistoricVideo: '/cameras/requestHistoricVideo',
  },
  devices: {
    realtime: '/devices/realtime',
    findAll: '/devices',
    activate: '/devices/activate',
    findAllByShareLinkUuid: {
      endpoint: '/devices/findAllByShareLinkUuid/:shareLinkUuid',
      params: ['shareLinkUuid']
    },
    findAllByLandmarkHistory: {
      endpoint: '/devices/findAllByLandmarkHistory/:eldLandmarkId/:fromDate/:toDate',
      params: ['eldLandmarkId', 'fromDate', 'toDate']
    },
    findAllByLandmark: {
      endpoint: '/devices/findAllByLandmark/:eldLandmarkId',
      params: ['eldLandmarkId']
    },
    updateDeviceType: '/devices/updateDeviceType',
    createShareLink: '/devices/createShareLink',
    createShareLinkMultiple: '/devices/createShareLinkMultiple',
    findByShareLinkUuid: {
      endpoint: '/devices/findByShareLinkUuid/:shareLinkUuid',
      params: ["shareLinkUuid"]
    },
    findAllHistory: {
      endpoint: '/devices/history/:eldDriverId/:date',
      params: ["eldDriverId", "date"]
    },
  },

  attachedFiles: {
    findByEntityId: {
      endpoint: '/attachedFiles/:entityType/:entityId',
      params: ['entityType', 'entityId'],
    },
    upload: {
      endpoint: '/attachedFiles/upload/:entityType/:entityId',
      params: ['entityType', 'entityId'],
    },
    delete: { endpoint: '/attachedFiles/:id', params: ['id'] },
  },
  organizations: {
    current: '/organizations/current',
    uploadFile: '/organizations/uploadFile',
    update: '/organizations',
  },
  statements: {
    findAll: '/statements',
    potentialStatementLines: {
      endpoint: '/statements/potentialStatementLines/:id',
      params: ['id'],
    },
    findAllHistoric: {
      endpoint: '/statements/historic/:fromDate/:toDate',
      params: ['fromDate', 'toDate'],
    },
    findById: { endpoint: '/statements/:id', params: ['id'] },
    update: '/statements',
    create: '/statements',
    delete: { endpoint: '/statements/:id', params: ['id'] },
    massDelete: '/statements/mass-delete',
    massUpdateStatus: '/statements/mass-update-status',
  },
  revenueReports: {
    findAll: '/revenueReports',
    potentialRevenueReportLines: {
      endpoint: '/revenueReports/potentialRevenueReportLines/:id',
      params: ['id'],
    },
    findAllHistoric: {
      endpoint: '/revenueReports/historic/:fromDate/:toDate',
      params: ['fromDate', 'toDate'],
    },
    findById: { endpoint: '/revenueReports/:id', params: ['id'] },
    update: '/revenueReports',
    create: '/revenueReports',
    delete: { endpoint: '/revenueReports/:id', params: ['id'] },
    massDelete: '/revenueReports/mass-delete',
    massUpdateStatus: '/revenueReports/mass-update-status',
  },
  loads: {
    findAll: '/loads',
    findAllHistoric: {
      endpoint: '/loads/historic/:fromDate/:toDate',
      params: ['fromDate', 'toDate'],
    },
    getDistance: {
      endpoint: '/loads/calculate-distance/:fromZip/:toZip',
      params: ['fromZip', 'toZip'],
    },
    findById: { endpoint: '/loads/:id', params: ['id'] },
    update: '/loads',
    create: '/loads',
    delete: { endpoint: '/loads/:id', params: ['id'] },
    getMaxLoadNumber: '/loads/max-load-number',

    massDelete: '/loads/mass-delete',
    massUpdateStatus: '/loads/mass-update-status',
  },
  iftaFilings: {
    findAll: '/iftaFilings',
    update: '/iftaFilings',
    create: '/iftaFilings',
    destroy: { endpoint: '/iftaFilings/:id', params: ['id'] },
    findById: { endpoint: '/iftaFilings/:id', params: ['id'] },
    uploadFile: '/iftaFilings/uploadFile',
  },

  rentalTrailers: {
    findAll: '/rentals/rentalTrailers',
    findAllInactive: '/rentals/rentalTrailers/inactive',
    update: '/rentals/rentalTrailers',
    create: '/rentals/rentalTrailers',
  },
  rentalCustomers: {
    uploadSignature: '/rentals/rentalCustomers',
    findAll: '/rentals/rentalCustomers',
    findAllInactive: '/rentals/rentalCustomers/inactive',
    update: '/rentals/rentalCustomers',
    create: '/rentals/rentalCustomers',
  },
  rentalOrganizationSettings: {
    current: '/rentals/rental-organization-settings/current',
    update: '/rentals/rental-organization-settings',
  },


  eldEventLines: {
    eventSuggested: '/eld/eldEventLines/eventSuggested',
    findLastActiveStatusEvent: {
      endpoint: '/eld/eldEventLines/lastActiveStatusLine/:eldDriverId',
      params: ['eldDriverId'],
    },
  },
  eldIftaReportCsv: {
    endpoint: '/eld/eldIftaReportCsv/:fromDate/:toDate',
    params: ['fromDate', 'toDate'],
  },
  eldIftaReportPdf: {
    endpoint: '/eld/eldIftaReportPdf/:fromDate/:toDate',
    params: ['fromDate', 'toDate'],
  },
  eldIftaReportPerVehicle: {
    endpoint: '/eld/eldIftaReportPerVehicle/:fromDate/:toDate',
    params: ['fromDate', 'toDate'],
  },
  eldIftaReportPerVehicleCsv: {
    endpoint: '/eld/eldIftaReportPerVehicleCsv/:fromDate/:toDate',
    params: ['fromDate', 'toDate'],
  },
  eldIftaReportPerVehiclePdf: {
    endpoint: '/eld/eldIftaReportPerVehiclePdf/:fromDate/:toDate',
    params: ['fromDate', 'toDate'],
  },
  eldDriverLogsSingle: {
    findAll: {
      endpoint: '/eld/eldDriverLogsSingle/:eldDriverId/:logDate',
      params: ['eldDriverId', 'logDate'],
    },

    downloadPdf: {
      endpoint: '/eld/eldDriverLogsSingle/downloadPdf/:eldDriverId/:logDate',
      params: ['eldDriverId', 'logDate'],
    },
  },
  eldDailyRecord: {
    updateDailyData: "/eld/eldDailyRecords/updateDailyData"
  },
  eldInspections: {
    upload: '/eld/eldInspections/upload',
    update: '/eld/eldInspections',
    downloadLogs: '/eld/eldInspections/downloadLogs',
    deleteInspection: 'eld/eldInspections/deleteInspection',
    sendLogsToFmcsa: '/eld/eldInspections/sendLogsToFmcsa',
    findAll: {
      endpoint: '/eld/eldInspections/:fromDate/:toDate',
      params: ['fromDate', 'toDate']
    },
  },
  eldVehicleDTCHistory: {
    findAll: '/eld/eldVehicleDTCHistory',
  },
  eldEngineImmobilizer: {
    requestUpdate: `/eld/eldEngineImmobilizer`,
    togglePower: `/eld/eldEngineImmobilizer/toggle`,
    findAll: `/eld/eldEngineImmobilizer`
  },
  eldLogs: {
    findAll: {
      endpoint: '/eld/eldDailyRecords/:fromDate/:toDate/:eldDriverId/:violationType',
      params: ['fromDate', 'toDate', 'eldDriverId', 'violationType'],
    },
  },
  eldAssetNotifications: {
    findAll: {
      endpoint: '/eld/eldAssetNotifications/:fromDate/:toDate/:deviceId/:eldLandmarkId',
      params: ['fromDate', 'toDate', 'deviceId', 'eldLandmarkId'],
    },
    update: '/eld/eldAssetNotifications',
  },
  eldDriverLogGroups: {
    findAll: '/eld/eldDriverLogGroups',
  },
  eldDriverLogsSummary: {
    findByEldDriverId: {
      endpoint: '/eld/eldDriverLogsSummary/:eldDriverId',
      params: ['eldDriverId'],
    },
  },
  eldUnidentifiedLogs: {
    findAll: {
      endpoint: '/eld/eldEventLinesUnidentified/:fromDate/:toDate/:organizationId',
      params: ['fromDate', 'toDate', 'organizationId']
    },
    update: `/eld/eldEventLinesUnidentified`,
  },
  eldOrganizationSettings: {
    current: '/eld/eld-organization-settings/current',
    update: '/eld/eld-organization-settings',
  },
  eldDormancySettings: {
    current: '/eld/eldDormancySettings/current',
    update: '/eld/eldDormancySettings',
  },
  eldDrivers: {
    uploadSignature: '/eld/uploadSignature',
    findAll: '/eld/eldDrivers',
    findAllInactive: '/eld/eldDrivers/inactive',
    update: '/eld/eldDrivers',
    create: '/eld/eldDrivers',
    generateMalfunctionLetter: {
      endpoint: '/eld/eldDrivers/generateMalfunctionLetter/:eldDriverId',
      params: ['eldDriverId'],
    },
  },
  eldDvirs: {
    findAll: {
      endpoint: '/eld/eldDvirs/:fromDate/:toDate',
      params: ['fromDate', 'toDate'],
    },
    update: '/eld/eldDvirs',
    create: '/eld/eldDvirs',
    delete: { endpoint: '/eld/eldDvirs/:id', params: ['id'] },
  },
  trailersPerTruckLines: {
    findAll: '/eld/eldTrailersPerTruckLines'
  },

  reports: {
    dormantAssets: {
      data: "/reports/dormant-assets/data",
      excel: "/reports/dormant-assets/excel",
    },
    harshBreaking: {
      data: "/reports/harsh-breaking/data",
      excel: "/reports/harsh-breaking/excel",
    },
    vehicleOdometer: {
      data: "/reports/vehicle-odometer/data",
      excel: "/reports/vehicle-odometer/excel",
    },
    iftaTrip: {
      data: "/reports/ifta-trip/data",
      excel: "/reports/ifta-trip/excel",
      states: "/reports/ifta-trip/states",
    },
    historyLines: {
      data: "/reports/device-history-lines/data",
      excel: "/reports/device-history-lines/excel",
      states: "/reports/device-history-lines/states"
    }
  },
  eldOrganizations: {
    childOrganizations: '/eld/eldOrganizations/childOrganizations'
  },
  eldConnectedVehicleUnitNumbers: {
    findAll: '/eld/eldConnectedVehicleUnitNumbers',
  },
  eldLandmarks: {
    findAll: '/eld/eldLandmarks',
    createShareLink: '/eld/eldLandmarks/createShareLink',
    findById: { endpoint: '/eld/eldLandmarks/:id', params: ['id'] },
    findAllWithAssetCount: '/eld/eldLandmarksWithAssetCount',
    findAllInactive: '/eld/eldLandmarks/inactive',
    update: '/eld/eldLandmarks',
    create: '/eld/eldLandmarks',
    delete: { endpoint: '/eld/eldLandmarks/:id', params: ['id'] },
  },
  eldVehicles: {
    uploadApiEnabledFile: '/eld/eldVehicles/uploadApiEnabledFile',
    findAll: '/eld/eldVehicles',
    update: '/eld/eldVehicles',
    create: '/eld/eldVehicles',
  },
  eldVehicleStats: {
    findAll: '/eld/eldVehicleStats',
    findByEldVehicleId: {
      endpoint: '/eld/eldVehicleStats/:eldVehicleId',
      params: ['eldVehicleId'],
    },
  },
  safetyTrips: {
    findByDeviceId: {
      endpoint: '/safety/safetyTripsFull/:deviceId/:date',
      params: ['deviceId', 'date'],
    },
  },
  deviceEldDriver: {
    findAll: '/deviceEldDriver'
  },
  eldUsers: {
    findAll: '/eld/eldUsers',
    create: '/eld/eldUsers',
    delete: { endpoint: '/eld/eldUsers/:id', params: ['id'] },
  },
  eldDevices: {
    findAll: '/eld/eldDevices',
    update: '/eld/eldDevices',
    create: '/eld/eldDevices',
    alarm: '/eld/eldDevices/alarm',
    assign: '/eld/eldDevices/assign'
  },
  drivers: {
    findAll: '/drivers',
    findAllInactive: '/drivers/inactive',
    update: '/drivers',
    create: '/drivers',
  },
  accidents: {
    findAll: '/accidents',
    findAllClosed: {
      endpoint: '/accidents/closed/:fromDate/:toDate',
      params: ['fromDate', 'toDate'],
    },
    update: '/accidents',
    create: '/accidents',
  },
  trucks: {
    findAll: '/trucks',
    findAllInactive: '/trucks/inactive',
    update: '/trucks',
    create: '/trucks',
  },
  debts: {
    findAll: '/debts',
    update: '/debts',
    create: '/debts',
    delete: { endpoint: '/debts/:id', params: ['id'] },
  },
  scheduledReports: {
    findAll: '/scheduledReports',
    update: '/scheduledReports',
    create: '/scheduledReports',
    delete: { endpoint: '/scheduledReports/:id', params: ['id'] },
  },
  maintenanceMechanics: {
    findAll: '/maintenance/mechanics',
    update: '/maintenance/mechanics',
    create: '/maintenance/mechanics',
  },
  maintenanceReminders: {
    findAll: '/maintenance/reminders',
  },
  maintenanceItems: {
    findAll: '/maintenance/items',
    findSuggestedForDevice: { endpoint: '/maintenance/items/suggested/:deviceId', params: ['deviceId'] },
    update: '/maintenance/items',
    create: '/maintenance/items',
    delete: { endpoint: '/maintenance/items/:id', params: ['id'] },
  },
  maintenanceGroups: {
    findAll: '/maintenance/groups',
    update: '/maintenance/groups',
    create: '/maintenance/groups',
    delete: { endpoint: '/maintenance/groups/:id', params: ['id'] },
  },
  maintenanceWorkOrders: {
    findAll: { endpoint: '/maintenance/:fromDate/:toDate', params: ['fromDate', 'toDate'] },
    findById: { endpoint: '/maintenance/:id', params: ['id'] },
    update: '/maintenance',
    create: '/maintenance',
    delete: { endpoint: '/maintenance/:id', params: ['id'] },
  },
  reminders: {
    replaceReminder: '/reminders/replaceReminder',
    ignoreReminder: '/reminders/ignoreReminder',
    findAll: '/reminders',
  },
  equipment: {
    findAll: '/equipment',
    findAllInactive: '/equipment/inactive',
    update: '/equipment',
    create: '/equipment',
  },
  businessPartners: {
    findAll: '/businessPartners',
    update: '/businessPartners',
    create: '/businessPartners',
  },
  deductions: {
    findAll: { endpoint: '/deductions/:fromDate/:toDate', params: ['fromDate', 'toDate'] },
    update: '/deductions',
    create: '/deductions',
    destroy: { endpoint: '/deductions/:id', params: ['id'] },
  },
  scheduledDeductions: {
    findAll: '/scheduledDeductions',
    update: '/scheduledDeductions',
    create: '/scheduledDeductions',
    destroy: { endpoint: '/scheduledDeductions/:id', params: ['id'] },
  },
  organizationUsers: {
    findById: { endpoint: '/organizationUsers/:id', params: ['id'] },
    findAll: '/organizationUsers',
    update: '/organizationUsers',
    create: '/organizationUsers',
    destroy: { endpoint: '/organizationUsers/:id', params: ['id'] },
  },
  credits: {
    findAll: { endpoint: '/credits/:fromDate/:toDate', params: ['fromDate', 'toDate'] },
    update: '/credits',
    create: '/credits',
    destroy: { endpoint: '/credits/:id', params: ['id'] },
  },
  fuelCharges: {
    findAll: { endpoint: '/fuelCharges/:fromDate/:toDate', params: ['fromDate', 'toDate'] },
    upload: '/fuelCharges/upload',
    update: '/fuelCharges',
    create: '/fuelCharges',
    massCreate: '/fuelCharges/massCreate',
    destroy: { endpoint: '/fuelCharges/:id', params: ['id'] },
  },
  tollCharges: {
    findAll: { endpoint: '/tollCharges/:fromDate/:toDate', params: ['fromDate', 'toDate'] },
    update: '/tollCharges',
    create: '/tollCharges',
    destroy: { endpoint: '/tollCharges/:id', params: ['id'] },
  },
  users: {
    forgotPassword: '/forgotPassword',
    resetPasswordUuid: '/resetPasswordUuid',
    resetPassword: '/resetPassword',
    currentForEdit: '/currentForEdit',
    updateCurrent: '/updateCurrent',
    updatePassword: '/users/updatePassword',
  },
};
